import { useState, useCallback, useRef, useEffect } from 'react';
import { api } from '../api/client';
import { useRefreshOnReturn } from './useRefreshOnReturn';
import { useVisiblePolling } from './useVisiblePolling';
import { getSseBus } from '../lib/sseBus';
import type { SessionInfo, AgentState } from '../api/types';

/**
 * Merge the local and federated session snapshots by their canonical agent name.
 *
 * Session names are federation-wide identities. During reconcile, `/remote` can
 * nevertheless contain a copy of a locally-known session (or the same peer can
 * be carried forward from cache). Returning both copies gives React lists
 * duplicate `key={session.name}` values; when a picker is filtered, React may
 * then retain a stale row for a non-matching agent. Keep the authoritative local
 * record when names overlap and preserve the existing local-first ordering.
 */
export function mergeUniqueSessions(
  localSessions: SessionInfo[],
  remoteSessions: SessionInfo[],
): SessionInfo[] {
  const byName = new Map<string, SessionInfo>();
  for (const session of localSessions) byName.set(session.name, session);
  for (const session of remoteSessions) {
    if (!byName.has(session.name)) byName.set(session.name, session);
  }
  return Array.from(byName.values());
}

/**
 * Live sessions + agent_states map.
 *
 * Now SSE-first (Cloud's `/api/sessions/events` Phase 1, IACP cc91e50e):
 *   - Initial `snapshot` event hydrates state atomically
 *   - `session.created` / `session.killed` mutate the list
 *   - `session.state` patches `agent_states[name]`
 *   - `session.status` patches `sessions[].status`
 *   - `session.pane_dirty` is for `useSessionV2` (ignored here)
 *
 * Polling stays as a 30 s fallback so a silent SSE drop or a legacy
 * backend without the endpoint still keeps the UI fresh — at a much
 * gentler cadence than the prior 5 s.
 */
export function useSessions() {
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [agentStates, setAgentStates] = useState<Record<string, AgentState>>({});
  const [error, setError] = useState<string | null>(null);
  const remoteRef = useRef<SessionInfo[]>([]);
  const remoteStatesRef = useRef<Record<string, AgentState>>({});

  const refresh = useCallback(async () => {
    try {
      const data = await api.sessions.all();
      const localSessions = data.sessions;
      const localStates = data.agent_states || {};
      setError(null);

      let remoteSessions = remoteRef.current;
      let remoteStates = remoteStatesRef.current;
      let remoteOk = false;
      try {
        const remote = await api.sessions.remote();
        const fetchedSessions = remote.sessions || [];
        const fetchedStates = remote.agent_states || {};
        const nodeStatus = remote.node_status || {};

        // Node-aware merge — the heart of the cloud2.mac arkserver-flicker
        // fix (Alex 2026-06-17). A blind replace dropped a peer's agents
        // whenever THAT peer was momentarily unreachable in this fetch,
        // even though other peers answered fine. Instead: keep cached
        // agents for any node that is NOT confirmed-ok this fetch, and
        // adopt fresh agents only for nodes that answered ok. Mirrors the
        // backend's "unreachable node carries its agents forward".
        const okNodes = new Set(
          Object.entries(nodeStatus).filter(([, v]) => v.status === 'ok').map(([n]) => n),
        );
        // If node_status is absent (older peer mid-rollout), fall back to
        // the old whole-empty guard so we don't regress.
        if (Object.keys(nodeStatus).length === 0) {
          const hadCachedData = remoteRef.current.length > 0;
          if (!(hadCachedData && fetchedSessions.length === 0)) {
            remoteSessions = fetchedSessions;
            remoteRef.current = fetchedSessions;
          }
        } else {
          // Start from fresh agents whose node answered ok…
          const merged: SessionInfo[] = fetchedSessions.filter(
            (s) => !s.node || okNodes.has(s.node),
          );
          const seenNodes = new Set(merged.map((s) => s.node));
          // …then carry forward cached agents from nodes that did NOT
          // answer ok this tick (momentarily unreachable — not gone).
          for (const cached of remoteRef.current) {
            if (cached.node && !okNodes.has(cached.node) && !seenNodes.has(cached.node)) {
              merged.push(cached);
            }
          }
          remoteSessions = merged;
          remoteRef.current = merged;
        }
        if (Object.keys(fetchedStates).length > 0) {
          remoteStates = { ...remoteStatesRef.current, ...fetchedStates };
          remoteStatesRef.current = remoteStates;
        }
        remoteOk = true;
      } catch { /* total fetch failure — keep cached remote */ }

      // Non-destructive when /remote failed with no cache: the SSE
      // snapshot is FEDERATION-WIDE (all nodes' agents, verified), so it
      // may already hold the remote agents. A blind [local-only] replace
      // would drop them — exactly what made arkserver vanish once the 30s
      // poll that used to retry /remote was removed (Alex 2026-06-19; also
      // the older "arkserver erscheint/verschwindet" flicker). So only do
      // the authoritative full replace when we actually have remote data;
      // otherwise refresh local in place and KEEP the remote agents the
      // snapshot already delivered.
      if (remoteOk || remoteRef.current.length > 0) {
        setSessions(mergeUniqueSessions(localSessions, remoteSessions));
      } else {
        const localNames = new Set(localSessions.map((s) => s.name));
        setSessions((prev) => {
          const keptRemote = prev.filter(
            (s) => !localNames.has(s.name) && !!s.node && s.node !== 'mac' && s.node !== 'local',
          );
          return [...localSessions, ...keptRemote];
        });
      }
      setAgentStates({ ...remoteStates, ...localStates });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'API unreachable');
    }
  }, []);

  // Subscribe to the SSE bus. Each event-type drives a targeted state
  // update so React only re-renders rows that actually changed. The
  // snapshot handler does a full-replace on initial connect AND on every
  // reconnect (Cloud's server replays snapshot when the last_event_id is
  // older than the ring-buffer — we treat it as the new truth).
  useEffect(() => {
    const bus = getSseBus();
    const unsubs: Array<() => void> = [];

    unsubs.push(bus.subscribe('snapshot', (ev) => {
      const incoming = ev.sessions || [];
      setError(null);
      // MERGE, don't replace. The SSE snapshot is per-node and on a
      // reconnect can arrive before the federation-disco has merged the
      // remote peers — a blind replace then dropped all arkserver/pdrei
      // agents until the next tick, making them flicker in and out (Alex
      // 2026-06-17: "arkserver-Agenten erscheinen und verschwinden").
      // We update/add the snapshot's agents but KEEP agents it doesn't
      // carry; real removals come from session.killed + the 30s poll
      // (which does a clean /all+/remote replace).
      if (incoming.length > 0) {
        setSessions((prev) => {
          const byName = new Map(prev.map((s) => [s.name, s]));
          for (const s of incoming) byName.set(s.name, s);
          return Array.from(byName.values());
        });
      }
      // agent_states likewise merged so remote dots don't blank to grey.
      if (ev.agent_states && Object.keys(ev.agent_states).length > 0) {
        setAgentStates((prev) => ({ ...prev, ...ev.agent_states }));
      }
    }));

    unsubs.push(bus.subscribe('session.created', (ev) => {
      setSessions((prev) => {
        if (prev.some((s) => s.name === ev.name)) return prev;
        return [...prev, ev.session];
      });
    }));

    unsubs.push(bus.subscribe('session.killed', (ev) => {
      setSessions((prev) => prev.filter((s) => s.name !== ev.name));
      setAgentStates((prev) => {
        if (!prev[ev.name]) return prev;
        const { [ev.name]: _gone, ...rest } = prev;
        void _gone;
        return rest;
      });
    }));

    unsubs.push(bus.subscribe('session.state', (ev) => {
      // Patch agent_states; the runtime state only lives there. Also
      // patch SessionInfo.activity_class when the fed-disco-diff packed
      // it into the same event (= remote-session form).
      setAgentStates((prev) => {
        const existing = prev[ev.name];
        const next: AgentState = {
          ...(existing || { state: 'unknown', detail: '' }),
          state: ev.state as AgentState['state'],
          detail: ev.detail ?? existing?.detail ?? '',
          // live_status is present only while thinking/tool and ABSENT
          // when ready/idle — so we set it from the event verbatim
          // (carrying it would leave a stale ticker after the turn ends).
          live_status: ev.live_status,
        };
        return { ...prev, [ev.name]: next };
      });
      // Patch SessionInfo fields the fed-disco-diff packed into session.state:
      // activity_class (dot age) AND the resume-at-tag pin state (Cloud
      // 2026-06-23). The pin fields flip live when an agent is rewound /
      // un-rewound so AgentDetail's pin banner reacts without a re-fetch.
      // `pinned_resume_tag` is sent verbatim — '' / null clears the pin.
      if (ev.activity_class !== undefined || ev.pinned_resume_tag !== undefined) {
        setSessions((prev) => {
          let touched = false;
          const next = prev.map((s) => {
            if (s.name !== ev.name) return s;
            const patch: Partial<SessionInfo> = {};
            if (ev.activity_class !== undefined && s.activity_class !== ev.activity_class) {
              patch.activity_class = ev.activity_class;
            }
            if (ev.pinned_resume_tag !== undefined && s.pinned_resume_tag !== ev.pinned_resume_tag) {
              patch.pinned_resume_tag = ev.pinned_resume_tag;
              patch.pinned_resume_session_id = ev.pinned_resume_session_id ?? null;
            }
            if (Object.keys(patch).length === 0) return s;
            touched = true;
            return { ...s, ...patch };
          });
          return touched ? next : prev;
        });
      }
    }));

    unsubs.push(bus.subscribe('session.status', (ev) => {
      setSessions((prev) => {
        let touched = false;
        const next = prev.map((s) => {
          if (s.name !== ev.name || s.status === ev.status) return s;
          touched = true;
          return { ...s, status: ev.status };
        });
        return touched ? next : prev;
      });
    }));

    return () => { for (const u of unsubs) u(); };
  }, []);

  // One bootstrap fetch on mount — hydrates the list before SSE delivers
  // its first snapshot (and serves as the data source on a legacy backend
  // without the event endpoint). No periodic poll any more (Alex
  // 2026-06-19): the SSE snapshot + session.* deltas are the live source.
  useEffect(() => { void refresh(); }, [refresh]);
  // Catch-up when the tab regains focus or the network returns — covers a
  // silently-stale SSE without a 30s interval. F5 is the manual backstop.
  useRefreshOnReturn(refresh);
  // Gentle reconcile poll (Alex 2026-06-23): SSE stays the live source, but a
  // MISSED corrective event leaves stale status forever (CloudM3 showed DEAD
  // while the backend reported running/ready — a transient session.status=dead
  // that was never un-set without a re-fetch). 45 s /all+/remote reconcile
  // corrects it; the node-aware merge above keeps it flicker-free.
  useVisiblePolling(refresh, 45000);

  return { sessions, agentStates, error, refresh };
}
